import { useEffect, useRef } from 'react';

import { C } from '../../../../../lib/design';
import { useActiveDocument } from '../../../../../context/ActiveDocumentContext';
import { Ic, ICON_PATHS } from '../../../../../lib/ui';
import { useI18n } from '../../../../../i18n';
import { generateViewerCSS } from '../../../../../config/documentStyles';
import type { Documento, DocumentVersion, DocumentoResumen } from '../../../../../types';
import { VersionsTab } from '../viewer/VersionsTab';
import { ReferencesTab } from '../viewer/ReferencesTab';
import { ArchivosTab } from '../viewer/ArchivosTab';
import { EditorToolbar } from '../viewer/EditorToolbar';
import { VIAPanel } from '../editor/VIAPanel';
import { ImportDocxDialog } from '../editor/ImportDocxDialog';
import { TableContextMenu } from '../editor/TableContextMenu';
import { useTenant } from '../../../../../providers/TenantProvider';
import { DocumentPanelHeader } from './DocumentPanelHeader';
import { DocumentContentView } from './DocumentContentView';
import { panelStyles } from './document-panel-styles';
import { useDocumentPanel } from './useDocumentPanel';

type PanelTab = 'contenido' | 'versiones' | 'referencias' | 'archivos';

interface DocumentPanelProps {
  documento: DocumentoResumen | null;
  onClose?: () => void;
  onExpandFullscreen?: () => void;
  externalHeader?: boolean;
  editingFromParent?: boolean;
  onEditingChange?: (editing: boolean) => void;
  onExportRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  onStateChange?: (state: PanelState) => void;
  useContentPages?: boolean;
  hideInternalControls?: boolean;
  readOnly?: boolean;
  viewerMode?: boolean;
  externalZoom?: number;
  onZoomChange?: (zoom: number) => void;
}

export interface PanelState {
  versions: DocumentVersion[];
  selectedVersionIndex: number;
  activeTab: PanelTab;
  doc: Documento | null;
  isEditing: boolean;
  saving: boolean;
  canEdit: boolean;
  lastSaved: Date | null;
  handlers: PanelHandlers;
}

export interface PanelHandlers {
  selectVersion: (index: number) => void;
  enterEditMode: () => void;
  exitEditMode: () => Promise<void>;
  discardChanges: () => Promise<void>;
  manualSave: () => Promise<void>;
  exportPdf: () => Promise<void>;
}

export interface PanelActions {
  selectVersion: (index: number) => void;
  setTab: (tab: PanelTab) => void;
  enterEditMode: () => void;
  exitEditMode: () => Promise<void>;
  discardChanges: () => Promise<void>;
  manualSave: () => Promise<void>;
  exportPdf: () => Promise<void>;
}

const viewerCSS = generateViewerCSS();

export function DocumentPanel({
  documento,
  onClose: _onClose,
  onExpandFullscreen,
  externalHeader = false,
  editingFromParent,
  onEditingChange,
  onExportRef,
  onStateChange,
  useContentPages: useContentPagesMode = false,
  hideInternalControls = false,
  readOnly = false,
  viewerMode = false,
  externalZoom,
  onZoomChange: onZoomChangeProp,
}: DocumentPanelProps) {
  const { t } = useI18n();
  const { tieneModulo } = useTenant();
  const copilotEnabled = tieneModulo('M9') && tieneModulo('M10') && !readOnly;
  const documentoCodigo = documento?.codigo ?? null;
  const panelRef = useRef<HTMLDivElement>(null);

  const h = useDocumentPanel({
    documentoCodigo,
    editingFromParent,
    onEditingChange,
    onExportRef,
    onStateChange,
    useContentPagesMode,
    externalZoom,
    onZoomChangeProp,
    panelRef,
  });

  const {
    setActiveDocument,
    clearActiveDocument,
    codigoDocumento: currentActiveCodigo,
  } = useActiveDocument();

  useEffect(() => {
    if (documentoCodigo) {
      setActiveDocument(documentoCodigo, h.doc?.titulo ?? documento?.titulo ?? null);
    }
    return () => {
      // Solo limpiar si el documento activo sigue siendo el nuestro — si otro
      // visor ya lo reemplazó (p.ej. al navegar de PE.01 a PE.02 sin cerrar el
      // panel) no borramos el contexto que otro acaba de publicar.
      if (currentActiveCodigo === documentoCodigo) {
        clearActiveDocument();
      }
    };
  }, [
    documentoCodigo,
    h.doc?.titulo,
    documento?.titulo,
    currentActiveCodigo,
    setActiveDocument,
    clearActiveDocument,
  ]);

  if (!documentoCodigo) {
    return (
      <div style={panelStyles.container}>
        <div style={panelStyles.placeholder}>
          <Ic d={ICON_PATHS.doc} s={48} c={C.textMut} />
          <p style={panelStyles.placeholderText}>{t.common.selectDocFromList}</p>
        </div>
      </div>
    );
  }

  if (h.loading) {
    return (
      <div style={panelStyles.container}>
        <div style={panelStyles.placeholder}>
          <p style={panelStyles.placeholderText}>{t.common.loadingDocument}</p>
        </div>
      </div>
    );
  }

  if (h.error) {
    return (
      <div style={panelStyles.container}>
        <div style={panelStyles.placeholder}>
          <p style={{ ...panelStyles.placeholderText, color: '#DC2626' }}>{h.error}</p>
        </div>
      </div>
    );
  }

  if (!h.doc) {
    return (
      <div style={panelStyles.container}>
        <div style={panelStyles.placeholder}>
          <p style={panelStyles.placeholderText}>{t.common.documentNotFound}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={panelRef} style={panelStyles.container}>
      <style>{viewerCSS}</style>
      <style>{`
        .dpanel-btn:hover { background: ${C.borderAlt} !important; }
        .dpanel-btn-primary:hover { opacity: 0.9 !important; }
        .dpanel-toolbar-btn:hover { background: ${C.border} !important; }
        .dpanel-toolbar-btn.active { background: ${C.border}; color: ${C.text}; }
        .dpanel-tab:hover { color: ${C.azul2} !important; }
        .dpanel-wf-btn:hover { opacity: 0.85; }
        .dpanel-version-opt:hover { background: ${C.borderAlt}; }
      `}</style>

      {!externalHeader && (
        <DocumentPanelHeader
          doc={h.doc}
          versionsCount={h.versions.length}
          activeTab={h.activeTab}
          setActiveTab={h.setActiveTab}
          zoom={h.zoom}
          zoomIn={h.zoomIn}
          zoomOut={h.zoomOut}
          isEditing={h.isEditing}
          saving={h.saving}
          exportingWord={h.exportingWord}
          lastSaved={h.lastSaved}
          canEdit={h.canEdit}
          readOnly={readOnly}
          viewerMode={viewerMode}
          showVIA={h.showVIA}
          refCount={h.refCount}
          onEnterEditMode={h.enterEditMode}
          onExitEditMode={h.exitEditMode}
          onManualSave={h.handleManualSave}
          onDiscardOpen={() => h.setDiscardConfirmOpen(true)}
          onToggleVIA={() => h.setShowVIA((v) => !v)}
          onExportWord={h.handleExportWord}
          onExportPdf={h.handleExportPdf}
          onExpandFullscreen={onExpandFullscreen}
          // 0661.1: el wrapper provee historial popover. Cuando el usuario
          // selecciona versión histórica, el padre puede sincronizar via este
          // callback (busca por versionId en h.versions y setea index).
          onSelectVersion={(versionId) => {
            const idx = h.versions.findIndex((v) => v.id === versionId);
            if (idx >= 0) h.handleSelectVersion(idx);
          }}
          aprobadoresEntidadTipo="version_documento"
          aprobadoresEntidadId={h.selectedVersion?.id}
          versionActivaEstado={h.displayEstado ?? null}
        />
      )}

      {!externalHeader &&
        h.isSelectedVersionVigente &&
        h.borradorVersion &&
        !h.isEditing &&
        !viewerMode && (
          <div
            style={{
              padding: '6px 16px',
              background: '#FFF7ED',
              borderBottom: '1px solid #FDBA74',
              fontSize: 11,
              color: '#9A3412',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexShrink: 0,
            }}
          >
            <span>&#9888;</span>
            <span>
              {t.viewer.borradorEnEdicion} (Ed.{' '}
              {String(h.borradorVersion.numeroEdicion).padStart(2, '0')})
            </span>
            <button
              onClick={() => {
                const idx = h.versions.findIndex(
                  (v) => v.estado === 'BORRADOR' || (v.estado as string) === 'EN_REVISION',
                );
                if (idx >= 0) h.setSelectedVersionIndex(idx);
              }}
              style={{
                fontSize: 11,
                color: '#2563EB',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontFamily: 'inherit',
              }}
            >
              {t.viewer.verBorrador}
            </button>
          </div>
        )}

      {externalHeader && !hideInternalControls && (
        <div style={panelStyles.tabBar}>
          {(['contenido', 'versiones', 'referencias', 'archivos'] as PanelTab[])
            .filter((tab) => !viewerMode || tab !== 'versiones')
            .map((tab) => {
              const isActive = h.activeTab === tab;
              const labels: Record<PanelTab, string> = {
                contenido: t.viewer.tabs.content,
                versiones: `${t.viewer.tabs.versions} (${h.versions.length})`,
                referencias: `${t.viewer.tabs.references}${h.refCount > 0 ? ` (${h.refCount})` : ''}`,
                archivos: t.archivos.tabArchivos,
              };
              return (
                <button
                  key={tab}
                  className="dpanel-tab"
                  onClick={() => h.setActiveTab(tab)}
                  style={{
                    ...panelStyles.tabBtn,
                    color: isActive ? C.azul2 : C.textSec,
                    borderBottom: isActive ? `2px solid ${C.azul2}` : '2px solid transparent',
                    fontWeight: isActive ? 600 : 400,
                  }}
                >
                  {labels[tab]}
                </button>
              );
            })}
        </div>
      )}

      {h.isEditing && h.editor && (
        <EditorToolbar
          editor={h.editor}
          onImportDocx={() => h.setShowImportDocx(true)}
          onToggleVIA={() => h.setShowVIA((v) => !v)}
        />
      )}

      {h.isEditing && <TableContextMenu editor={h.editor} />}

      {h.activeTab === 'contenido' && (
        <>
          {externalHeader && !hideInternalControls && (
            <div style={panelStyles.zoomBar}>
              <button onClick={h.zoomOut} style={panelStyles.zoomBtn}>
                {'\u2212'}
              </button>
              <span style={panelStyles.zoomLabel}>{h.zoom}%</span>
              <button onClick={h.zoomIn} style={panelStyles.zoomBtn}>
                +
              </button>
            </div>
          )}
          <DocumentContentView
            doc={h.doc}
            estado={h.estado}
            selectedVersion={h.selectedVersion}
            isEditing={h.isEditing}
            editor={h.editor}
            zoom={h.zoom}
            contentHtml={h.contentHtml}
            contentBlocksA4={h.contentBlocksA4}
            useContentPagesMode={useContentPagesMode}
            copilotEnabled={copilotEnabled}
            editionLabel={h.editionLabel}
            dateLabel={h.dateLabel}
          />
        </>
      )}

      {h.activeTab === 'versiones' && (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <VersionsTab
            versions={h.versions}
            selectedVersionIndex={h.selectedVersionIndex}
            onSelectVersion={h.handleSelectVersion}
            versionVigenteId={(h.doc as any)?.versionVigenteId}
          />
        </div>
      )}

      {h.activeTab === 'referencias' && (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <ReferencesTab
            referenciasSalida={h.doc.referenciasSalida ?? []}
            referenciasEntrada={h.doc.referenciasEntrada ?? []}
          />
        </div>
      )}

      {h.activeTab === 'archivos' && h.doc && (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <ArchivosTab codigoDocumento={h.doc.codigo} readOnly={h.estado !== 'BORRADOR'} />
        </div>
      )}

      {h.discardConfirmOpen && (
        <div onClick={() => h.setDiscardConfirmOpen(false)} style={panelStyles.modalOverlay}>
          <div onClick={(e) => e.stopPropagation()} style={panelStyles.discardDialog}>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 700, color: C.azul1 }}>
              {t.common.descartarCambiosDialogTitle}
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: C.textSec }}>
              {t.common.descartarCambiosDialogDesc}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => h.setDiscardConfirmOpen(false)} style={panelStyles.btn}>
                {t.common.cancelar}
              </button>
              <button
                onClick={h.discardChanges}
                style={{ ...panelStyles.btnPrimary, backgroundColor: '#DC2626' }}
              >
                {t.common.discard}
              </button>
            </div>
          </div>
        </div>
      )}

      {h.toast && (
        <div style={panelStyles.toast}>
          <Ic d={ICON_PATHS.check} s={14} c="#fff" />
          {h.toast}
        </div>
      )}

      {h.showVIA && (
        <div style={panelStyles.viaPanelOverlay}>
          <div style={panelStyles.viaPanelBackdrop} onClick={() => h.setShowVIA(false)} />
          <div style={panelStyles.viaPanelContainer}>
            <VIAPanel
              open={h.showVIA}
              onToggle={() => h.setShowVIA((v) => !v)}
              codigoDocumento={documentoCodigo ?? ''}
              versionId={h.selectedVersion?.id ?? ''}
              selectionInfo={null}
              rolUsuario="CEO"
              onSugerenciasGeneradas={() => {}}
              mode={h.isEditing ? 'edit' : 'read'}
            />
          </div>
        </div>
      )}

      <ImportDocxDialog
        open={h.showImportDocx}
        onClose={() => h.setShowImportDocx(false)}
        onImport={(content) => {
          if (h.editor) h.editor.commands.setContent(content as any);
        }}
      />
    </div>
  );
}

export default DocumentPanel;
